import React, { useState, useEffect } from 'react';
import { Button, Card, Form, Input, message } from 'antd';
import { FaQuestionCircle } from 'react-icons/fa';
import drEasyApi from '../../Api/Customapi';
import getErrorMessage from '../Errors Handling/getErrorMssg';
import { LuClipboardPenLine } from 'react-icons/lu';
import formImg from "../../assets/formImg.png";
import { useLanguage } from '../../context/LanguageContext';

const FormAnswer = ({ doctorId, onSubmit, onSkip }) => {
  const { t } = useLanguage();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const response = await drEasyApi.get(`/api/Book/forms/${doctorId}`);
        setFormData(response.data);
      } catch (error) {
        console.error('Error fetching form:', error);
        setFetchError(getErrorMessage(error, t('booking.form.fetchError')));
        message.error(getErrorMessage(error, t('booking.form.fetchError')));
      } finally {
        setFetchLoading(false);
      }
    };

    if (doctorId) {
      fetchForm();
    }
  }, [doctorId, t]);

  const handleSubmit = async (values) => {
    if (!formData) return;

    try {
      setLoading(true);
      const answers = formData.questions.map((question) => ({
        questionId: question.questionId,
        answerText: values[question.questionId] || '',
      }));

      const payload = {
        doctorId,
        answers,
      };

      await drEasyApi.post(`/api/Book/forms/answers`, payload);
      message.success(t('booking.form.submitSuccess'));
      form.resetFields();
      onSubmit();
    } catch (error) {
      console.error('Form submission error:', error);
      message.error(getErrorMessage(error, t('booking.form.submitError')));
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return <div className="text-center p-8 text-textPrimary">{t('booking.form.loading')}</div>;
  }

  if (fetchError) {
    return <div className="text-center p-8 text-red-600">{fetchError}</div>;
  }

  if (!formData || !formData.questions.length) {
    return <div className="text-center p-8 text-textPrimary">{t('booking.form.noQuestions')}</div>;
  }

  return (
    <section className="relative w-full min-h-screen overflow-hidden px-4 py-10 flex justify-center">
      {/* Background Image */}
      <div className="absolute inset-0 z-0 opacity-15 pointer-events-none">
        <img
          src={formImg}
          alt="Floating form"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none animate-float"
        />
      </div>

      {/* Foreground Content */}
      <div className="relative z-10 max-w-4xl w-full flex flex-col gap-5">
        <span className='space-y-3'>
          <h2 className="text-2xl font-bold text-textPrimary text-center flex justify-center gap-2">
            <LuClipboardPenLine className="text-3xl text-textPrimary" /> {t('booking.form.title')}
          </h2>
          <p className='text-center text-placeHolder mt-1'>{t('booking.form.description')}</p>
        </span>

        <Card className="bg-white/90 rounded-2xl shadow-lg border border-primary/30">
          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={handleSubmit}
            className="space-y-6"
          >
            {formData.questions.map((question) => (
              <Form.Item
                key={question.questionId}
                name={question.questionId}
                label={
                  <span className="flex items-center gap-2 text-textPrimary font-medium">
                    <FaQuestionCircle className="text-primary" size={16} />
                    {question.questionText}
                  </span>
                }
                rules={[{ required: true, message: t('booking.form.answerRequired') }]}
              >
                <Input
                  placeholder={t('booking.form.placeholder')}
                  className="rounded-lg border border-placeHolder focus:border-primary focus:ring-1 focus:ring-primary transition duration-200"
                />
              </Form.Item>
            ))}

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="w-full bg-primary hover:bg-primary_hover h-12 text-lg font-semibold rounded-xl shadow-md"
              >
                {t('booking.form.submitButton')}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </section>
  );
};

export default FormAnswer;  